import { inject, Injectable } from '@angular/core';
import { ExcelDownloadRequest, ExcelExportOptions, ExcelResult } from '@model/excel-download-request.model';
import { IP } from '@model/ftf-api.model';
import { ApiService } from '@service/api.service';
import { LoggerService } from '@service/logger.service';
import { from, Observable, of } from 'rxjs';
import { catchError, filter, mergeMap } from 'rxjs/operators';

import { ExcelDownloadService } from './excel-download.service';

@Injectable({
  providedIn: 'root',
})
export class ExcelService {
  private logger = inject(LoggerService);
  private apiService = inject(ApiService);
  private excelDownloadService = inject(ExcelDownloadService);

  /**
   * Convert a request into an observable that will emit a stream of the idenifiers to process.
   * Handles all the different data source types and the family expansion logic, so the resulting
   * stream is always a flat list of case/matter/trademark identifiers in string form, ready for the next step.
   *
   * @param request The Excel download request containing the data source and template type.
   * @returns An observable that emits a stream of identifiers as strings.
   */
  buildDataSourceObservable(request: ExcelDownloadRequest): Observable<string> {
    const ds = request.dataSource;

    // reference docs are odd one.. so build their observable here
    if (request.templateType === 'ReferenceDocument') {
      return from([...new Set(ds.items as string[])]);
    }

    // get identifiers in string form
    const items = ds.type === 'Case' ? ds.items.map((item) => item.identifier) : ds.items;
    this.logger.info(`[buildDataSourceObservable] Starting with ${items.length} items, includeFamilyCases: ${request.includeFamilyCases}`);
    items.forEach((i) => this.logger.info(`  - ${i}`));

    let ipType: IP.TrifTypeEnum = IP.TrifTypeEnum.Patent;
    if (request.templateType === 'IPCaseDocuments') {
      // eslint-disable-next-line prefer-destructuring
      ipType = request.ipType;
    } else if (request.templateType === 'ExternalLink') {
      // eslint-disable-next-line prefer-destructuring
      ipType = request.ipType;
    } else if (request.templateType === 'ReferenceAssociation') {
      ipType = IP.TrifTypeEnum.Patent;
    } else if (request.templateType === 'TrademarkImage') {
      ipType = IP.TrifTypeEnum.Trademark;
    }

    // normalize and load potential families
    return from(items).pipe(
      mergeMap((identifier) => {
        if (!request.includeFamilyCases) {
          return of(identifier);
        }
        return this.apiService.findFamilyAssociations(identifier, ipType).pipe(
          mergeMap((familyIds) => from([...new Set([identifier, ...familyIds])])),
          catchError(() => of(identifier))
        );
      }),
      filter((item): item is string => item !== null)
    );
  }

  /**
   * Entry point for all Excel download prepare actions.
   *
   * If the data source is ManualInput or SheetInput, resolves the raw case numbers to
   * CommonIP objects via the API (in parallel) before processing.
   */
  executeDownloadRequest(request: ExcelDownloadRequest, options?: ExcelExportOptions): Observable<ExcelResult> {
    const source$ = this.buildDataSourceObservable(request);
    return this.excelDownloadService.handleRequest(request, source$, options);
  }

  listSheetsWithColumns(): Observable<{ id: string; name: string; columnLetters: string[] }[]> {
    return new Observable((observer) => {
      Excel.run(async (context) => {
        try {
          const sheets = context.workbook.worksheets;
          sheets.load('items/name');
          await context.sync();

          const result = sheets.items.map((sheet) => {
            return {
              id: sheet.id,
              name: sheet.name,
              columnLetters: this.generateColumnLetters(26),
            };
          });

          observer.next(result);
          observer.complete();
        } catch (err) {
          observer.error(err);
        }
      });
    });
  }

  private generateColumnLetters(count: number): string[] {
    const letters: string[] = [];
    for (let i = 0; i < count; i++) {
      letters.push(String.fromCharCode(65 + i)); // 65 = 'A'
    }
    return letters;
  }

  readColumnRange(sheetId: string, columnLetter: string, startRow: number, rowCount: number): Observable<string[]> {
    return new Observable((observer) => {
      Excel.run(async (context) => {
        try {
          const sheet = context.workbook.worksheets.getItem(sheetId);

          const endRow = startRow + rowCount - 1;
          const rangeAddress = `${columnLetter}${startRow}:${columnLetter}${endRow}`;
          const range = sheet.getRange(rangeAddress);
          range.load('values');

          await context.sync();

          const result = range.values
            .flat()
            .map((v) => (v ? v.toString().trim() : ''))
            .filter((v) => v.length > 0);

          observer.next(result);
          observer.complete();
        } catch (err) {
          observer.error(err);
        }
      });
    });
  }
}
